import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Program {
  id?: string;
  name: string;
  description: string;
  mission: string;
  target_population: string;
  geographic_scope: string;
  start_date: string;
  end_date: string;
  budget: string;
  status: string;
}

interface ProgramFormProps {
  program?: Program;
  onSubmit: (data: Partial<Program>) => Promise<void>;
  isLoading?: boolean;
}

const emptyProgram: Program = {
  name: '',
  description: '',
  mission: '',
  target_population: '',
  geographic_scope: '',
  start_date: '',
  end_date: '',
  budget: '',
  status: 'active',
};

export function ProgramForm({ program, onSubmit, isLoading }: ProgramFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Program>(program || emptyProgram);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (program) {
      setFormData(program);
    }
  }, [program]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Program name is required');
      return;
    }

    try {
      await onSubmit({
        name: formData.name,
        description: formData.description || null,
        mission: formData.mission || null,
        target_population: formData.target_population || null,
        geographic_scope: formData.geographic_scope || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        status: formData.status,
      } as any);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save program');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Program Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="e.g., Youth Mentorship Program"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Brief overview of the program"
            />
          </div>

          <div>
            <label htmlFor="mission" className="block text-sm font-medium text-gray-700">
              Mission Statement
            </label>
            <textarea
              id="mission"
              name="mission"
              rows={2}
              value={formData.mission}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="What this program aims to achieve"
            />
          </div>
        </div>
      </div>

      {/* Target & Scope */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Target & Scope</h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="target_population" className="block text-sm font-medium text-gray-700">
              Target Population
            </label>
            <input
              type="text"
              id="target_population"
              name="target_population"
              value={formData.target_population}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="e.g., At-risk youth ages 12-18"
            />
          </div>

          <div>
            <label htmlFor="geographic_scope" className="block text-sm font-medium text-gray-700">
              Geographic Scope
            </label>
            <input
              type="text"
              id="geographic_scope"
              name="geographic_scope"
              value={formData.geographic_scope}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="e.g., Metro Atlanta area"
            />
          </div>
        </div>
      </div>

      {/* Timeline & Budget */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline & Budget</h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank for ongoing programs</p>
          </div>

          <div>
            <label htmlFor="budget" className="block text-sm font-medium text-gray-700">
              Budget
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status (only show for edit) */}
      {program?.id && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status</h3>
          
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Program Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => navigate('/programs')}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : program?.id ? 'Update Program' : 'Create Program'}
        </button>
      </div>
    </form>
  );
}
